import React, { createContext, useState, useContext, useEffect, useRef, useCallback } from 'react';
import { toast } from 'sonner';
import { supabase } from './supabase';
import { clearQueryCache, clearAuthCaches } from './cacheManager';
import { logger } from './logger';
import { getAuthErrorMessage } from './authMessages';

export const AuthContext = createContext();

// 30-min inactivity: sessions older than this are rejected on restore (stricter security)
const LAST_ACTIVITY_KEY = 'kc_last_activity';
const INACTIVITY_MS = 30 * 60 * 1000;
const PERSIST_THROTTLE_MS = 1000;

// Ctrl+Shift+R sets this in sessionStorage; when set, we do full clear on next load instead of using cached session
const FULL_RESET_SESSION_KEY = 'kconnect_full_reset';

// Agent-log ingest: disabled. 127.0.0.1 is blocked by CSP in prod and can cause thrown errors → reload.
// Re-enable only when VITE_INGEST_URL is set to a CSP-allowed URL.
const INGEST_ENABLED = false;

const persistLastActivity = (timestamp = Date.now()) => {
  try {
    localStorage.setItem(LAST_ACTIVITY_KEY, String(timestamp));
  } catch (e) {
    logger.debug('Could not persist last activity:', e.message);
  }
};

const getLastActivity = () => {
  try {
    const ts = localStorage.getItem(LAST_ACTIVITY_KEY);
    return ts ? parseInt(ts, 10) : null;
  } catch (e) {
    return null;
  }
};

const isSessionRestoreAllowed = () => {
  const lastActivity = getLastActivity();
  if (lastActivity == null) return true; // No timestamp = first run / bootstrap, allow
  return (Date.now() - lastActivity) <= INACTIVITY_MS;
};

// Helper to add timeout protection to promises
const withTimeout = (promise, timeoutMs = 8000) => {
  return Promise.race([
    promise,
    new Promise((_, reject) =>
      setTimeout(() => reject(new Error('Request timed out')), timeoutMs)
    )
  ]);
};

// Run a promise-returning fn with retry on failure
const withRetry = async (promiseFn, { retries = 1, delayMs = 2000 } = {}) => {
  let lastError;
  for (let attempt = 0; attempt <= retries; attempt++) {
    try {
      return await promiseFn();
    } catch (e) {
      lastError = e;
      if (attempt < retries) {
        await new Promise((r) => setTimeout(r, delayMs));
      }
    }
  }
  throw lastError;
};

// Safely get session without hanging
// timeoutMs: optional - 30s default for visibility/refresh checks, 5s/8s for initial auth
const safeGetSession = async (timeoutMs = 30000) => {
  // #region agent log
  (INGEST_ENABLED && fetch('http://127.0.0.1:7242/ingest/8376e0bb-afab-45bb-a3b7-b026c69e0da0',{method:'POST',headers:{'Content-Type':'application/json'},body:JSON.stringify({location:'AuthContext.jsx:20',message:'safeGetSession: Entry',data:{},timestamp:Date.now(),sessionId:'debug-session',runId:'run1',hypothesisId:'B'})}).catch(()=>{}));
  // #endregion
  try {
    const result = await withTimeout(supabase.auth.getSession(), timeoutMs);
    // #region agent log
    (INGEST_ENABLED && fetch('http://127.0.0.1:7242/ingest/8376e0bb-afab-45bb-a3b7-b026c69e0da0',{method:'POST',headers:{'Content-Type':'application/json'},body:JSON.stringify({location:'AuthContext.jsx:22',message:'safeGetSession: Success',data:{hasSession:!!result.data?.session},timestamp:Date.now(),sessionId:'debug-session',runId:'run1',hypothesisId:'B'})}).catch(()=>{}));
    // #endregion
    return result;
  } catch (error) {
    // #region agent log
    (INGEST_ENABLED && fetch('http://127.0.0.1:7242/ingest/8376e0bb-afab-45bb-a3b7-b026c69e0da0',{method:'POST',headers:{'Content-Type':'application/json'},body:JSON.stringify({location:'AuthContext.jsx:24',message:'safeGetSession: Timeout or error',data:{errorMessage:error.message},timestamp:Date.now(),sessionId:'debug-session',runId:'run1',hypothesisId:'B'})}).catch(()=>{}));
    // #endregion
    logger.error('getSession timed out or failed:', error.message);
    return { data: { session: null }, error };
  }
};

// Safely validate user without hanging
const safeGetUser = async () => {
  try {
    return await withTimeout(supabase.auth.getUser(), 30000);
  } catch (error) {
    logger.error('getUser timed out or failed:', error.message);
    return { data: { user: null }, error };
  }
};

export const AuthProvider = ({ children }) => {
  const [user, setUser] = useState(null);
  const [session, setSession] = useState(null);
  const [userProfile, setUserProfile] = useState(null);
  const [isAuthenticated, setIsAuthenticated] = useState(false);
  const [isLoading, setIsLoading] = useState(true);
  const [authError, setAuthError] = useState(null);
  const [isReconnecting, setIsReconnecting] = useState(false);
  const [passwordRecoveryMode, setPasswordRecoveryMode] = useState(false);
  // Password expiry state: { isExpired, isWarning, daysRemaining }
  const [passwordExpiryStatus, setPasswordExpiryStatus] = useState(null);
  // MFA state: { needsEnrollment, needsChallenge, currentLevel, nextLevel }
  const [mfaStatus, setMfaStatus] = useState(null);

  // Track if we're currently refreshing to prevent duplicate refresh attempts
  const isRefreshing = useRef(false);
  // Track last activity for proactive refresh
  const lastActivityRef = useRef(Date.now());
  // Track if onAuthStateChange has already provided a valid session
  // This prevents initializeAuth from clearing a valid session on timeout
  const hasReceivedAuthEvent = useRef(false);
  // 'soft' = normal F5, no getSession; 'full_reset' = Ctrl+Shift+R; 'full_fetch' = legacy path (unused now for normal load)
  const initPathRef = useRef('full_fetch');
  // Throttle for persisting last activity to localStorage
  const lastPersistTimeRef = useRef(0);
  // Refs to track current state for use in onAuthStateChange callback
  // (callback closes over initial values, so we need refs for latest values)
  const userProfileRef = useRef(null);
  const userRef = useRef(null);
  const isAuthenticatedRef = useRef(false);

  // Ctrl+Shift+R: set flag so next load does full reset (clear session) instead of using cached session
  useEffect(() => {
    const handleKeyDown = (e) => {
      if (e.key === 'R' && e.ctrlKey && e.shiftKey) {
        try {
          sessionStorage.setItem(FULL_RESET_SESSION_KEY, '1');
          logger.debug('Full reset requested (Ctrl+Shift+R)');
        } catch (err) {
          logger.debug('Could not set full reset flag:', err?.message);
        }
      }
    };
    window.addEventListener('keydown', handleKeyDown);
    return () => window.removeEventListener('keydown', handleKeyDown);
  }, []);

  // Proactively refresh session when user returns after inactivity
  const refreshSessionIfNeeded = useCallback(async () => {
    if (isRefreshing.current) return;

    try {
      isRefreshing.current = true;
      logger.debug('Checking if session refresh is needed...');

      const { data: { session: currentSession }, error: sessionError } = await safeGetSession();

      if (sessionError || !currentSession) {
        logger.debug('No valid session during refresh check');
        // Don't aggressively clear - just log and let user continue
        // They'll get redirected on next API call if truly invalid
        return;
      }

      // Check if access token is close to expiring (within 2 minutes)
      const expiresAt = currentSession.expires_at;
      const now = Math.floor(Date.now() / 1000);
      const timeUntilExpiry = expiresAt - now;

      if (timeUntilExpiry < 120) { // Less than 2 minutes until expiry
        logger.debug('Access token expiring soon, refreshing...');
        // Use 8s timeout when app was backgrounded > 5 min (slow reconnect on mobile)
        const timeSinceLastActivity = Date.now() - lastActivityRef.current;
        const refreshTimeout = timeSinceLastActivity > 5 * 60 * 1000 ? 8000 : 5000;
        try {
          const { data, error: refreshError } = await withTimeout(
            supabase.auth.refreshSession(),
            refreshTimeout
          );

          if (refreshError || !data?.session) {
            logger.error('Token refresh failed:', refreshError?.message);
            // Don't clear session here - let it fail naturally on API calls
            return;
          }

          logger.debug('Session refreshed successfully');
          setSession(data.session);
        } catch (timeoutErr) {
          logger.error('Token refresh timed out');
          // Don't clear - might just be network issue
        }
      } else {
        logger.debug('Session still valid, no refresh needed');
      }
    } catch (error) {
      logger.error('Session refresh check error:', error);
      // Don't aggressively clear on errors
    } finally {
      isRefreshing.current = false;
    }
  }, []);

  // Keep refs in sync with state for use in onAuthStateChange callback
  useEffect(() => {
    userProfileRef.current = userProfile;
    userRef.current = user;
    isAuthenticatedRef.current = isAuthenticated;
  }, [userProfile, user, isAuthenticated]);

  const clearAuthError = useCallback(() => {
    setAuthError(null);
  }, []);

  // Set up visibility change listener for proactive session refresh + 30-min inactivity check
  useEffect(() => {
    const handleVisibilityChange = () => {
      if (document.visibilityState === 'visible') {
        // 30-min inactivity: if user was away > 30 min, clear session for security
        if (isAuthenticated && !isSessionRestoreAllowed()) {
          logger.debug('Session expired due to 30-min inactivity - clearing');
          clearInvalidSession();
          return;
        }
        const timeSinceLastActivity = Date.now() - lastActivityRef.current;
        lastActivityRef.current = Date.now();
        // If more than 2 minutes since last activity, check session
        if (timeSinceLastActivity > 2 * 60 * 1000 && isAuthenticated) {
          logger.debug('App became visible after inactivity, checking session...');
          refreshSessionIfNeeded();
        }
      }
    };

    const handleUserActivity = () => {
      lastActivityRef.current = Date.now();
      // Persist last activity (throttled) for 30-min restore check
      const now = Date.now();
      if (now - lastPersistTimeRef.current >= PERSIST_THROTTLE_MS) {
        lastPersistTimeRef.current = now;
        persistLastActivity(now);
      }
    };

    document.addEventListener('visibilitychange', handleVisibilityChange);
    // Track user activity (matches useInactivityTimeout events)
    window.addEventListener('click', handleUserActivity);
    window.addEventListener('mousedown', handleUserActivity);
    window.addEventListener('mousemove', handleUserActivity);
    window.addEventListener('keydown', handleUserActivity);
    window.addEventListener('scroll', handleUserActivity);
    window.addEventListener('touchstart', handleUserActivity);

    return () => {
      document.removeEventListener('visibilitychange', handleVisibilityChange);
      window.removeEventListener('click', handleUserActivity);
      window.removeEventListener('mousedown', handleUserActivity);
      window.removeEventListener('mousemove', handleUserActivity);
      window.removeEventListener('keydown', handleUserActivity);
      window.removeEventListener('scroll', handleUserActivity);
      window.removeEventListener('touchstart', handleUserActivity);
    };
  }, [isAuthenticated, refreshSessionIfNeeded]);

  useEffect(() => {
    // #region agent log
    (INGEST_ENABLED && fetch('http://127.0.0.1:7242/ingest/8376e0bb-afab-45bb-a3b7-b026c69e0da0',{method:'POST',headers:{'Content-Type':'application/json'},body:JSON.stringify({location:'AuthContext.jsx:157',message:'useEffect: Starting auth initialization',data:{timestamp:Date.now()},timestamp:Date.now(),sessionId:'debug-session',runId:'run1',hypothesisId:'A'})}).catch(()=>{}));
    // #endregion
    initializeAuth();

    const { data: { subscription } } = supabase.auth.onAuthStateChange(
      async (event, session) => {
        // #region agent log
        (INGEST_ENABLED && fetch('http://127.0.0.1:7242/ingest/8376e0bb-afab-45bb-a3b7-b026c69e0da0',{method:'POST',headers:{'Content-Type':'application/json'},body:JSON.stringify({location:'AuthContext.jsx:162',message:'onAuthStateChange: Event received',data:{event,hasSession:!!session,hasUser:!!session?.user,hasReceivedAuthEvent:hasReceivedAuthEvent.current},timestamp:Date.now(),sessionId:'debug-session',runId:'run1',hypothesisId:'C'})}).catch(()=>{}));
        // #endregion
        logger.debug('Auth state changed:', event);

        // Handle token refresh errors
        if (event === 'TOKEN_REFRESHED') {
          logger.debug('Token was refreshed successfully');
          lastActivityRef.current = Date.now();
        }

        // Detect password recovery event - redirect to set-password page
        if (event === 'PASSWORD_RECOVERY') {
          logger.debug('Password recovery mode - redirecting to /set-password');
          if (window.location.pathname !== '/set-password') {
            // Preserve hash (e.g. #access_token=...&refresh_token=...) so SetPassword page can establish session
            const hash = window.location.hash ? window.location.hash : '';
            window.location.href = '/set-password' + hash;
            return; // Don't continue processing, page will reload
          }
        }

        // Clear recovery mode when user updates their password
        if (event === 'USER_UPDATED' && passwordRecoveryMode) {
          logger.debug('Password updated, clearing recovery mode');
          setPasswordRecoveryMode(false);
        }

        // Handle sign out event explicitly
        if (event === 'SIGNED_OUT') {
          logger.debug('User signed out');
          setSession(null);
          setUser(null);
          setUserProfile(null);
          setIsAuthenticated(false);
          setIsLoading(false);
          return;
        }

        setSession(session);
        if (session?.user) {
          // 30-min inactivity: reject sessions when last activity was > 30 min ago
          if (!isSessionRestoreAllowed()) {
            logger.debug('Session expired due to 30-min inactivity (onAuthStateChange) - clearing');
            hasReceivedAuthEvent.current = false;
            await clearInvalidSession();
            setIsLoading(false);
            return;
          }
          persistLastActivity(Date.now());

          // Mark that we've received a valid auth event
          hasReceivedAuthEvent.current = true;

          // Skip profile fetch if we already have a valid profile for this user
          // This prevents unnecessary re-fetches when tab regains focus
          // Use refs to get current values (callback closes over initial state)
          if (userProfileRef.current && userRef.current?.id === session.user.id && isAuthenticatedRef.current) {
            logger.debug('Auth event received, but already have valid profile - skipping fetch');
            setIsLoading(false);
            // #region agent log
            (INGEST_ENABLED && fetch('http://127.0.0.1:7242/ingest/8376e0bb-afab-45bb-a3b7-b026c69e0da0',{method:'POST',headers:{'Content-Type':'application/json'},body:JSON.stringify({location:'AuthContext.jsx:207',message:'onAuthStateChange: Skipping profile fetch, isLoading false',data:{},timestamp:Date.now(),sessionId:'debug-session',runId:'run1',hypothesisId:'C'})}).catch(()=>{}));
            // #endregion
            return;
          }

          // #region agent log
          (INGEST_ENABLED && fetch('http://127.0.0.1:7242/ingest/8376e0bb-afab-45bb-a3b7-b026c69e0da0',{method:'POST',headers:{'Content-Type':'application/json'},body:JSON.stringify({location:'AuthContext.jsx:211',message:'onAuthStateChange: Calling loadUserProfile',data:{userId:session.user?.id},timestamp:Date.now(),sessionId:'debug-session',runId:'run1',hypothesisId:'C'})}).catch(()=>{}));
          // #endregion
          await loadUserProfile(session.user, session.access_token);
        } else {
          setUser(null);
          setUserProfile(null);
          setIsAuthenticated(false);
        }
        setIsLoading(false);
        // #region agent log
        (INGEST_ENABLED && fetch('http://127.0.0.1:7242/ingest/8376e0bb-afab-45bb-a3b7-b026c69e0da0',{method:'POST',headers:{'Content-Type':'application/json'},body:JSON.stringify({location:'AuthContext.jsx:217',message:'onAuthStateChange: isLoading set to false',data:{event,hasSession:!!session},timestamp:Date.now(),sessionId:'debug-session',runId:'run1',hypothesisId:'C'})}).catch(()=>{}));
        // #endregion
      }
    );

    return () => subscription.unsubscribe();
  }, []);

  const initializeAuth = async () => {
    // #region agent log
    (INGEST_ENABLED && fetch('http://127.0.0.1:7242/ingest/8376e0bb-afab-45bb-a3b7-b026c69e0da0',{method:'POST',headers:{'Content-Type':'application/json'},body:JSON.stringify({location:'AuthContext.jsx:224',message:'initializeAuth: Entry',data:{hasReceivedAuthEvent:hasReceivedAuthEvent.current},timestamp:Date.now(),sessionId:'debug-session',runId:'run1',hypothesisId:'A'})}).catch(()=>{}));
    // #endregion
    try {
      logger.debug('Starting auth initialization...');
      setIsLoading(true);

      // Full reset: user pressed Ctrl+Shift+R - clear session and show login (no refetch)
      if (typeof sessionStorage !== 'undefined' && sessionStorage.getItem(FULL_RESET_SESSION_KEY)) {
        sessionStorage.removeItem(FULL_RESET_SESSION_KEY);
        initPathRef.current = 'full_reset';
        logger.debug('Full reset (Ctrl+Shift+R) - clearing session');
        await clearInvalidSession({ removeLastActivity: true });
        setIsLoading(false);
        return;
      }

      // Normal F5 / refresh: do not refetch session - rely on onAuthStateChange(INITIAL_SESSION) from storage.
      // This avoids the 5s+8s getSession timeout path that caused login screen to reload after ~10s.
      initPathRef.current = 'soft';
      const SAFETY_TIMEOUT_MS = 30000;
      setTimeout(() => {
        if (!hasReceivedAuthEvent.current) {
          logger.debug('No auth event received within 30s - stopping loading');
          setIsLoading(false);
        }
      }, SAFETY_TIMEOUT_MS);
      return;
    } catch (error) {
      logger.error('Auth initialization error:', error);
      if (!hasReceivedAuthEvent.current) {
        setAuthError(error.message);
        setSession(null);
        setUser(null);
        setUserProfile(null);
        setIsAuthenticated(false);
      }
    } finally {
      // Only clear loading here when we did not take the soft path (soft path clears via timer or onAuthStateChange)
      if (initPathRef.current !== 'soft') {
        setIsLoading(false);
      }
      logger.debug('Auth initialization complete');
    }
  };

  // Clear invalid/stale session data - uses timeout to prevent hanging
  // options.removeLastActivity: when false, do not remove kc_last_activity (so late onAuthStateChange
  // still sees it and 30-min rule applies). Use false only when clearing due to "no session after 15s" timeout.
  const clearInvalidSession = async (options = {}) => {
    const { removeLastActivity = true } = options;
    logger.debug('Clearing invalid session...');

    // Reset state immediately - don't wait for signOut
    setSession(null);
    setUser(null);
    setUserProfile(null);
    setIsAuthenticated(false);
    setIsLoading(false);

    // Try to sign out locally with timeout - but don't block on it
    try {
      await withTimeout(
        supabase.auth.signOut({ scope: 'local' }),
        3000 // Short timeout - if it hangs, just move on
      );
    } catch (e) {
      // Ignore errors - state is already cleared
      logger.debug('signOut timed out or failed (state already cleared):', e.message);
    }

    // As a last resort, manually clear Supabase localStorage if signOut didn't work
    try {
      const storageKey = `sb-${new URL(import.meta.env.VITE_SUPABASE_URL).hostname.split('.')[0]}-auth-token`;
      localStorage.removeItem(storageKey);
      if (removeLastActivity) {
        localStorage.removeItem(LAST_ACTIVITY_KEY);
      }
      logger.debug('Manually cleared Supabase auth token from localStorage');
    } catch (e) {
      logger.debug('Could not clear localStorage:', e.message);
    }
  };

  const refreshPasswordExpiryStatus = useCallback(async (userId) => {
    if (!userId) return;
    try {
      const { data: expiryData, error: expiryError } = await supabase.rpc(
        'get_password_expiry_status',
        { p_user_id: userId }
      );

      if (!expiryError && expiryData?.[0]) {
        const status = expiryData[0];
        setPasswordExpiryStatus({
          isExpired: status.is_expired,
          isWarning: status.is_warning,
          daysRemaining: status.days_remaining,
          daysSinceChange: status.days_since_change
        });
        logger.debug('Password expiry status:', {
          daysRemaining: status.days_remaining,
          isExpired: status.is_expired,
          isWarning: status.is_warning
        });
      } else {
        setPasswordExpiryStatus(null);
        if (expiryError) {
          logger.debug('Could not check password expiry:', expiryError.message);
        }
      }
    } catch (expiryCheckError) {
      logger.debug('Password expiry check failed:', expiryCheckError.message);
      setPasswordExpiryStatus(null);
    }
  }, []);

  const loadUserProfile = async (authUser, accessToken) => {
    // #region agent log
    (INGEST_ENABLED && fetch('http://127.0.0.1:7242/ingest/8376e0bb-afab-45bb-a3b7-b026c69e0da0',{method:'POST',headers:{'Content-Type':'application/json'},body:JSON.stringify({location:'AuthContext.jsx:342',message:'loadUserProfile: Entry',data:{userId:authUser?.id,hasAccessToken:!!accessToken},timestamp:Date.now(),sessionId:'debug-session',runId:'run1',hypothesisId:'B'})}).catch(()=>{}));
    // #endregion
    try {
      logger.debug('Starting loadUserProfile...');
      setUser(authUser);
      setIsAuthenticated(true);

      const startTime = Date.now();

      logger.debug('Fetching user profile...');

      // Use optimized RPC function to bypass complex RLS policies on app_users
      // The is_conversation_participant policy causes 10-15 second delays
      // Retry once on timeout for transient network issues
      // #region agent log
      (INGEST_ENABLED && fetch('http://127.0.0.1:7242/ingest/8376e0bb-afab-45bb-a3b7-b026c69e0da0',{method:'POST',headers:{'Content-Type':'application/json'},body:JSON.stringify({location:'AuthContext.jsx:354',message:'loadUserProfile: Starting RPC call',data:{startTime},timestamp:Date.now(),sessionId:'debug-session',runId:'run1',hypothesisId:'B'})}).catch(()=>{}));
      // #endregion
      let profiles;
      let fetchError;
      try {
        const result = await withRetry(
          () => withTimeout(supabase.rpc('get_my_profile'), 15000),
          { retries: 1, delayMs: 2000 }
        );
        profiles = result?.data;
        fetchError = result?.error;
      } catch (retryErr) {
        if (retryErr?.message?.includes('timed out')) {
          logger.debug('Profile fetch timed out after retry');
          fetchError = retryErr;
          profiles = null;
        } else {
          throw retryErr;
        }
      }

      const endTime = Date.now();
      // #region agent log
      (INGEST_ENABLED && fetch('http://127.0.0.1:7242/ingest/8376e0bb-afab-45bb-a3b7-b026c69e0da0',{method:'POST',headers:{'Content-Type':'application/json'},body:JSON.stringify({location:'AuthContext.jsx:359',message:'loadUserProfile: RPC call completed',data:{elapsed:endTime-startTime,hasError:!!fetchError,errorCode:fetchError?.code,hasProfile:!!profiles?.[0]},timestamp:Date.now(),sessionId:'debug-session',runId:'run1',hypothesisId:'B'})}).catch(()=>{}));
      // #endregion
      logger.debug(`Profile fetch completed in ${endTime - startTime}ms`);

      if (fetchError) {
        logger.error('Profile fetch error:', fetchError.code);

        // If we get auth error, the session is invalid - clear it
        if (fetchError.code === 'PGRST301' || fetchError.message?.includes('JWT')) {
          logger.error('Auth error - clearing invalid session');
          await clearInvalidSession();
          return;
        }

        throw fetchError;
      }

      const profile = profiles?.[0] || null;

      if (!profile) {
        logger.debug('No profile found for user');
        setUserProfile(null);
        setIsLoading(false);
        return;
      }

      // Update status from 'invited' or null to 'active' on first successful login
      if (!profile.metadata?.status || profile.metadata?.status === 'invited') {
        logger.debug('Updating user status to active...');
        try {
          const { error: statusError } = await withTimeout(
            supabase
              .from('app_users')
              .update({ metadata: { ...profile.metadata, status: 'active' } })
              .eq('id', authUser.id),
            5000
          );

          if (statusError) {
            logger.error('Failed to update user status');
          } else {
            profile.metadata = { ...profile.metadata, status: 'active' };
            logger.debug('User status updated to active');
          }
        } catch (e) {
          logger.error('Status update timed out:', e.message);
        }
      }

      if (profile && !profile.current_branch_id && profile.branch_ids && profile.branch_ids.length > 0) {
        logger.debug('Setting default branch...');
        const defaultBranchId = profile.branch_ids[0];

        try {
          const { error: updateError } = await withTimeout(
            supabase
              .from('app_users')
              .update({ current_branch_id: defaultBranchId })
              .eq('id', authUser.id),
            5000
          );

          if (updateError) {
            logger.error('Failed to set default branch');
            // Continue without throwing, as the app can still function with the client-side fallback
          } else {
            // Update the local profile object so the change is reflected immediately
            profile.current_branch_id = defaultBranchId;
            logger.debug('Default branch set successfully');
          }
        } catch (e) {
          logger.error('Default branch update timed out:', e.message);
        }
      }

      // Update last_login_at timestamp (fire-and-forget, don't block login)
      supabase
        .from('app_users')
        .update({ last_login_at: new Date().toISOString() })
        .eq('id', authUser.id)
        .then(({ error: loginTrackError }) => {
          if (loginTrackError) {
            logger.debug('Failed to update last_login_at:', loginTrackError.message);
          }
        });

      // Check password expiry status (90-day rotation policy)
      await refreshPasswordExpiryStatus(authUser.id);

      // Check secure login method status for admin users
      const isSuperAdmin = profile.primary_role === 'super_admin';
      const isBranchAdmin = profile.primary_role === 'branch_admin';
      const isAdmin = isSuperAdmin || isBranchAdmin;
      
      if (isAdmin) {
        try {
          // Get enabled states from preferences
          const preferences = profile.preferences || {};
          const secureAuth = preferences.secure_auth || {};
          const mfaEnabled = secureAuth.mfa_enabled !== false; // Default to true for MFA (admins)

          const { data: aalData, error: aalError } = await supabase.auth.mfa.getAuthenticatorAssuranceLevel();

          if (!aalError && aalData) {
            const { currentLevel, nextLevel } = aalData;

            // Check if user has MFA enrolled
            const { data: factorsData } = await supabase.auth.mfa.listFactors();
            const hasVerifiedFactor = factorsData?.totp?.some(f => f.status === 'verified') || false;

            // For branch_admin, check if they have passkey(s)
            let hasBiometrics = false;
            if (isBranchAdmin) {
              try {
                const { data: biometricData, error: biometricError } = await supabase
                  .from('webauthn_credentials')
                  .select('id')
                  .eq('user_id', profile.id)
                  .limit(1);
                
                hasBiometrics = !biometricError && biometricData && biometricData.length > 0;
              } catch (biometricCheckError) {
                logger.debug('Biometric check failed:', biometricCheckError.message);
                hasBiometrics = false;
              }
            }

            // Biometrics: default OFF. If user has passkey(s), treat as ON unless they explicitly turned it off.
            const biometricEnabled = hasBiometrics ? (secureAuth.biometric_enabled !== false) : (secureAuth.biometric_enabled === true);

            logger.debug(`Secure login status for ${profile.primary_role}:`, { 
              currentLevel, 
              nextLevel, 
              hasVerifiedFactor, 
              hasBiometrics,
              mfaEnabled,
              biometricEnabled
            });

            // Super admin: MFA is mandatory (if enabled)
            if (isSuperAdmin) {
              if (mfaEnabled && !hasVerifiedFactor) {
                // Super admin without MFA - needs to enroll
                setMfaStatus({
                  needsEnrollment: true,
                  needsChallenge: false,
                  currentLevel,
                  nextLevel
                });
              } else if (mfaEnabled && hasVerifiedFactor && currentLevel === 'aal1' && nextLevel === 'aal2') {
                // Has MFA enabled and enrolled but not verified in this session - needs to verify
                setMfaStatus({
                  needsEnrollment: false,
                  needsChallenge: true,
                  currentLevel,
                  nextLevel
                });
              } else {
                // MFA verified (aal2) or MFA disabled
                setMfaStatus({
                  needsEnrollment: false,
                  needsChallenge: false,
                  currentLevel,
                  nextLevel
                });
              }
            } 
            // Branch admin: At least one secure method required (MFA OR biometrics) - but only if enabled
            else if (isBranchAdmin) {
              const hasEnabledMFA = mfaEnabled && hasVerifiedFactor;
              const hasEnabledBiometric = biometricEnabled && hasBiometrics;
              
              if (!hasEnabledMFA && !hasEnabledBiometric) {
                // Branch admin without any enabled secure method - needs to enroll (can choose MFA or biometrics)
                setMfaStatus({
                  needsEnrollment: true,
                  needsChallenge: false,
                  currentLevel,
                  nextLevel,
                  allowSkip: true // Allow skipping MFA if they set up biometrics instead
                });
              } else if (mfaEnabled && hasVerifiedFactor && currentLevel === 'aal1' && nextLevel === 'aal2') {
                // Has MFA enabled and enrolled but not verified in this session - needs to verify
                setMfaStatus({
                  needsEnrollment: false,
                  needsChallenge: true,
                  currentLevel,
                  nextLevel
                });
              } else {
                // Has at least one enabled secure method (MFA verified OR biometrics enabled)
                setMfaStatus({
                  needsEnrollment: false,
                  needsChallenge: false,
                  currentLevel,
                  nextLevel
                });
              }
            }
          } else {
            setMfaStatus(null);
          }
        } catch (mfaError) {
          logger.debug('MFA check failed:', mfaError.message);
          setMfaStatus(null);
        }
      } else {
        // Non-admin users don't need MFA
        setMfaStatus(null);
      }

      logger.debug('Profile loaded successfully');
      setUserProfile(profile);
      setIsLoading(false);
      // #region agent log
      (INGEST_ENABLED && fetch('http://127.0.0.1:7242/ingest/8376e0bb-afab-45bb-a3b7-b026c69e0da0',{method:'POST',headers:{'Content-Type':'application/json'},body:JSON.stringify({location:'AuthContext.jsx:598',message:'loadUserProfile: Success - isLoading set to false',data:{profileId:profile?.id},timestamp:Date.now(),sessionId:'debug-session',runId:'run1',hypothesisId:'B'})}).catch(()=>{}));
      // #endregion
    } catch (error) {
      // #region agent log
      (INGEST_ENABLED && fetch('http://127.0.0.1:7242/ingest/8376e0bb-afab-45bb-a3b7-b026c69e0da0',{method:'POST',headers:{'Content-Type':'application/json'},body:JSON.stringify({location:'AuthContext.jsx:599',message:'loadUserProfile: Exception caught',data:{errorMessage:error.message,isTimeout:error.message?.includes('timed out'),hasExistingProfile:!!userProfile},timestamp:Date.now(),sessionId:'debug-session',runId:'run1',hypothesisId:'B'})}).catch(()=>{}));
      // #endregion
      logger.error('Error loading user profile:', error);

      // If profile loading fails completely, clear the session to prevent blank pages
      if (error.message?.includes('401') || error.message?.includes('403') || error.message?.includes('JWT')) {
        setAuthError(error.message);
        await clearInvalidSession();
      } else if (error.message?.includes('timed out')) {
        // On timeout, check if we already have a valid profile
        // If yes, keep the user logged in - this is likely just a network hiccup
        // after tab was backgrounded. Don't kick out users who are already authenticated.
        if (userProfile) {
          logger.debug('Profile fetch timed out, but user already has valid profile - keeping session');
          setIsLoading(false);
          // Don't set authError - user can continue using the app
        } else {
          // No existing profile - this is initial login, allow retry
          logger.debug('Profile fetch timed out during initial login - allowing retry');
          setAuthError(error.message);
          setIsLoading(false);
          // #region agent log
          (INGEST_ENABLED && fetch('http://127.0.0.1:7242/ingest/8376e0bb-afab-45bb-a3b7-b026c69e0da0',{method:'POST',headers:{'Content-Type':'application/json'},body:JSON.stringify({location:'AuthContext.jsx:618',message:'loadUserProfile: Timeout during initial login - isLoading false',data:{},timestamp:Date.now(),sessionId:'debug-session',runId:'run1',hypothesisId:'B'})}).catch(()=>{}));
          // #endregion
          // Don't clear auth state - let user retry by refreshing
        }
      } else {
        setAuthError(error.message);
        setIsLoading(false);
        // #region agent log
        (INGEST_ENABLED && fetch('http://127.0.0.1:7242/ingest/8376e0bb-afab-45bb-a3b7-b026c69e0da0',{method:'POST',headers:{'Content-Type':'application/json'},body:JSON.stringify({location:'AuthContext.jsx:623',message:'loadUserProfile: Other error - isLoading false',data:{errorMessage:error.message},timestamp:Date.now(),sessionId:'debug-session',runId:'run1',hypothesisId:'B'})}).catch(()=>{}));
        // #endregion
      }
    }
  };

  const signUp = async (email, password, metadata = {}) => {
    try {
      setIsLoading(true);
      setAuthError(null);

      const { data, error } = await supabase.auth.signUp({
        email,
        password,
        options: { data: metadata }
      });

      if (error) throw error;

      if (data.user) {
        const { error: profileError } = await supabase
          .from('app_users')
          .insert({
            id: data.user.id,
            email: data.user.email,
            first_name: metadata.first_name || '',
            last_name: metadata.last_name || '',
            roles: [metadata.primary_role || 'parent'],
            primary_role: metadata.primary_role || 'parent',
            active_role: metadata.primary_role || 'parent'
          });

        if (profileError) throw profileError;
      }

      return data;
    } catch (error) {
      setAuthError(error.message);
      throw error;
    } finally {
      setIsLoading(false);
    }
  };

  const signIn = async (email, password) => {
    try {
      setIsLoading(true);
      setAuthError(null);

      const { data, error } = await supabase.auth.signInWithPassword({
        email,
        password
      });

      if (error) {
        // Check if account is locked (banned)
        if (error.message?.toLowerCase().includes('banned') ||
            error.message?.toLowerCase().includes('zablokowane') ||
            error.message?.toLowerCase().includes('user is banned')) {
          setAuthError('Konto użytkownika jest zablokowane.');
          throw new Error('ACCOUNT_LOCKED');
        }

        // Record failed login attempt (fire-and-forget, don't block)
        supabase.functions.invoke('handle-failed-login', {
          body: {
            email,
            user_agent: navigator.userAgent
          }
        }).then(response => {
          if (response.data?.locked) {
            logger.debug('Account has been locked after this attempt');
          } else if (response.data?.remainingAttempts !== undefined) {
            logger.debug(`Remaining login attempts: ${response.data.remainingAttempts}`);
          }
        }).catch(err => {
          logger.debug('Failed to record login attempt:', err.message);
        });

        throw error;
      }

      // Successful login - clear any previous failed attempts
      supabase.rpc('clear_failed_login_attempts', { p_email: email })
        .then(({ error: clearError }) => {
          if (clearError) {
            logger.debug('Failed to clear login attempts:', clearError.message);
          }
        });

      return data;
    } catch (error) {
      // Don't override the specific locked message
      if (error.message !== 'ACCOUNT_LOCKED') {
        // Translate common Supabase Auth errors to Polish
        const errorMessage = error.message === 'Invalid login credentials'
          ? 'Nieprawidłowe dane logowania'
          : error.message;
        setAuthError(errorMessage);
      }
      throw error;
    } finally {
      setIsLoading(false);
    }
  };

  const signOut = async (queryClient = null) => {
    try {
      setIsLoading(true);

      // Clear React Query cache first (while we still have context)
      if (queryClient) {
        clearQueryCache(queryClient);
      }

      // Clear state immediately - don't wait for network call
      setSession(null);
      setUser(null);
      setUserProfile(null);
      setIsAuthenticated(false);
      setPasswordExpiryStatus(null);
      setMfaStatus(null);

      // Clear any remaining auth caches
      clearAuthCaches();

      // Try to sign out with timeout - but don't block the user if it hangs
      try {
        await withTimeout(supabase.auth.signOut(), 5000);
      } catch (signOutError) {
        logger.debug('signOut timed out or failed (state already cleared):', signOutError.message);
        // Manually clear localStorage as fallback
        try {
          const storageKey = `sb-${new URL(import.meta.env.VITE_SUPABASE_URL).hostname.split('.')[0]}-auth-token`;
          localStorage.removeItem(storageKey);
        } catch (e) {
          // Ignore
        }
      }
    } catch (error) {
      setAuthError(error.message);
      throw error;
    } finally {
      setIsLoading(false);
    }
  };

  const updateProfile = async (updates) => {
    try {
      // Use user.id or fall back to userProfile.id (handles race conditions)
      const userId = user?.id || userProfile?.id;
      if (!userId) throw new Error('No user logged in');

      const { error } = await supabase
        .from('app_users')
        .update(updates)
        .eq('id', userId);

      if (error) throw error;

      // Get current session to pass access token
      const { data: { session } } = await safeGetSession();
      if (session?.access_token && user) {
        await loadUserProfile(user, session.access_token);
      } else if (userProfile) {
        // Refresh profile directly if we have userProfile but not user
        await refreshProfile();
      }
    } catch (error) {
      setAuthError(error.message);
      throw error;
    }
  };

  const updatePassword = async (newPassword) => {
    try {
      if (!user) throw new Error('No user logged in');

      const { data, error } = await supabase.auth.updateUser({
        password: newPassword
      });

      if (error) {
        // Translate Supabase auth errors to Polish
        const errorTranslations = {
          'New password should be different from the old password': 'Nowe hasło musi być inne niż poprzednie',
          'Password should be at least 6 characters': 'Hasło musi mieć co najmniej 6 znaków',
          'Password is too weak': 'Hasło jest zbyt słabe',
        };
        const translatedMessage = errorTranslations[error.message] || error.message;
        throw new Error(translatedMessage);
      }

      logger.debug('Password updated successfully');
      // Clear recovery mode after successful password update
      setPasswordRecoveryMode(false);

      const userId = data?.user?.id ?? user?.id;
      await refreshPasswordExpiryStatus(userId);

      return data;
    } catch (error) {
      logger.error('Error updating password:', error);
      setAuthError(error.message);
      throw error;
    }
  };

  const resetPasswordForEmail = async (email) => {
    try {
      setAuthError(null);

      logger.debug('Sending password reset email via Edge Function...');
      const startTime = Date.now();

      // Use custom Edge Function for branded email template
      const { data, error } = await supabase.functions.invoke('send-password-reset', {
        body: { email }
      });

      const endTime = Date.now();
      logger.debug(`Password reset request completed in ${endTime - startTime}ms`);

      if (error) throw error;
      if (data?.error) throw new Error(data.error);

      logger.debug('Password reset email sent successfully');
      return { success: true, data };
    } catch (error) {
      logger.error('Error sending password reset email:', error);
      setAuthError(error.message);
      throw error;
    }
  };

  const clearPasswordRecoveryMode = () => {
    setPasswordRecoveryMode(false);
  };

  const refreshProfile = useCallback(async () => {
    if (!user || !session?.access_token) return;
    await loadUserProfile(user, session.access_token);
  }, [user, session]);

  // Refresh MFA status after enrollment or verification
  const refreshMfaStatus = async () => {
    if (!userProfile || userProfile.primary_role !== 'super_admin') {
      setMfaStatus(null);
      return;
    }

    try {
      const { data: aalData, error: aalError } = await supabase.auth.mfa.getAuthenticatorAssuranceLevel();

      if (!aalError && aalData) {
        const { currentLevel, nextLevel } = aalData;
        const { data: factorsData } = await supabase.auth.mfa.listFactors();
        const hasVerifiedFactor = factorsData?.totp?.some(f => f.status === 'verified') || false;

        logger.debug('MFA status refreshed:', { currentLevel, nextLevel, hasVerifiedFactor });

        if (!hasVerifiedFactor) {
          setMfaStatus({ needsEnrollment: true, needsChallenge: false, currentLevel, nextLevel });
        } else if (currentLevel === 'aal1' && nextLevel === 'aal2') {
          setMfaStatus({ needsEnrollment: false, needsChallenge: true, currentLevel, nextLevel });
        } else {
          setMfaStatus({ needsEnrollment: false, needsChallenge: false, currentLevel, nextLevel });
        }
      }
    } catch (err) {
      logger.debug('MFA status refresh failed:', err.message);
    }
  };

  // Lightweight function to update only preferences without full profile refresh
  // This avoids the expensive loadUserProfile call that causes page reloads
  const updatePreferences = useCallback((newPreferences) => {
    setUserProfile(prev => {
      if (!prev) return prev;
      const updated = {
        ...prev,
        preferences: newPreferences
      };
      // Also update ref for consistency
      userProfileRef.current = updated;
      return updated;
    });
  }, []);

  const retryAuth = useCallback(async () => {
    clearAuthError();
    setIsLoading(true);
    await initializeAuth();
  }, [clearAuthError]);

  const value = {
    user,
    session,
    userProfile,
    isAuthenticated,
    isLoading,
    authError,
    isReconnecting,
    passwordRecoveryMode,
    passwordExpiryStatus,
    mfaStatus,
    signUp,
    signIn,
    signOut,
    updateProfile,
    updatePreferences,
    updatePassword,
    resetPasswordForEmail,
    clearPasswordRecoveryMode,
    clearAuthError,
    retryAuth,
    refreshProfile,
    refreshMfaStatus
  };

  return (
    <AuthContext.Provider value={value}>
      {children}
    </AuthContext.Provider>
  );
};

export const useAuth = () => {
  const context = useContext(AuthContext);
  if (!context) {
    throw new Error('useAuth must be used within an AuthProvider');
  }
  return context;
};
